/**
 * MultiMarkerARSession
 *
 * Main controller that integrates all multi-marker AR components.
 * Manages the complete AR session lifecycle, detection loop, and marker switching.
 */

import * as THREE from 'three';
import { MarkerConfigLoader } from './marker-config-loader.js';
import { MultiVideoController } from './multi-video-controller.js';
import { CloudflareStreamVideoController } from './cloudflare-stream-video-controller.js';
import { MarkerPriorityCalculator } from './marker-priority-calculator.js';
import { ActiveMarkerSelectionManager } from './active-marker-selection-manager.js';
import { PosterDetector } from '../core/detector.js';
import { PerformanceMonitor } from './performance-monitor.js';
import { DebugVisualizer } from './debug-visualizer.js';
import { ErrorHandler, ErrorType } from './error-handler.js';
import { FeedbackOverlay } from '../ui/feedback-overlay.js';
import { AudioConsentDialog } from '../components/audio-consent-dialog.js';
import { createCTAFromPosterData } from '../components/cta-overlay.js';
import {
  getCloudflareStreamConfig,
  isCloudflareStreamConfigured,
} from '../config/cloudflare-stream.config.js';
import { DeviceCapabilityDetector, ObjectPool } from '../utils/performance-utils.js';
import {
  QUALITY_PRESETS,
  DETECTION_FPS_DEFAULT,
  SELECTION_LOCK_DURATION,
} from '../config/performance.config.js';
import { VideoTextureManager } from '../core/renderer.js';
import { CameraMotionDetector } from '../utils/camera-motion-detector.js';
import { getDiagnostics } from '../utils/performance-diagnostics.js';

/**
 * デバッグログヘルパー。Viteの import.meta.env.DEV により本番ビルドで
 * no-op に置換され、コード自体もツリーシェイクで消える。
 */
const IS_DEV = typeof import.meta !== 'undefined' && !!import.meta.env && import.meta.env.DEV;
const debugLog = IS_DEV ? console.log.bind(console) : () => {};
const debugWarn = IS_DEV ? console.warn.bind(console) : () => {};

export class MultiMarkerARSession {
  /**
   * @param {Object} options - Session configuration
   * @param {string} options.configPath - Path to marker configuration JSON
   * @param {string} options.containerElementId - Container element ID for MindAR
   * @param {Function} options.onProgress - Progress callback (percent) => void
   * @param {Function} options.onMarkerChanged - Marker change callback (markerId) => void
   * @param {Function} options.onError - Error callback (error) => void
   * @param {number} options.detectionFps - Detection loop FPS (default: 15)
   * @param {boolean} options.enablePerformanceMonitoring - Enable performance monitoring (default: false)
   * @param {Function} options.onMetricsUpdate - Performance metrics callback (metrics) => void
   * @param {Function} options.onPerformanceDegradation - Performance degradation callback (quality tier) => void
   * @param {boolean} options.enableDebugVisualization - Enable debug visualization (default: false)
   */
  constructor(options) {
    this.options = options;
    this.configPath = options.configPath;
    this.containerElementId = options.containerElementId || 'mindar-container';

    // Callbacks
    this.onProgress = options.onProgress || null;
    this.onMarkerChanged = options.onMarkerChanged || null;
    this.onError = options.onError || null;

    // Components
    this.detector = null;
    this.videoController = null;
    this.selectionManager = null;
    this.performanceMonitor = null;
    this.debugVisualizer = null;
    this.errorHandler = null;
    this.feedbackOverlay = null;
    this.audioConsentDialog = null;
    this.config = null;

    // State
    this.isInitialized = false;
    this.isRunning = false;
    this.currentActiveMarkerId = null; // Track current active marker for fast switching
    this.userAudioChoice = null; // Store user's audio preference (true = muted, false = unmuted)

    // Loop control (T025: Dynamic FPS based on device tier)
    this.detectionFps = options.detectionFps || DETECTION_FPS_DEFAULT;
    this.detectionInterval = null;
    this.renderLoopId = null;
    this.deviceTier = null; // Will be set during init()

    // T020: Shared pose data between detection and render loops
    this.latestPoseData = null; // Latest pose matrices from detection loop
    this.poseDataLock = false; // Simple lock to prevent concurrent access

    // Performance monitoring
    this.enablePerformanceMonitoring = options.enablePerformanceMonitoring || false;
    this.onMetricsUpdate = options.onMetricsUpdate || null;
    this.onPerformanceDegradation = options.onPerformanceDegradation || null;

    // Debug visualization
    this.enableDebugVisualization = options.enableDebugVisualization || false;

    // T031: Three.js object pooling for multi-marker performance
    this.geometryPool = null; // Initialized after detector setup
    this.materialPool = null; // Initialized after detector setup

    // T032: Video texture manager for conditional updates
    this.videoTextureManager = new VideoTextureManager();

    // T033: Inactive marker cleanup tracking
    this.markerLastDetectedAt = new Map(); // markerId -> timestamp
    this.inactiveCleanupInterval = null; // Cleanup interval ID

    // Camera motion detection for stable marker selection
    this.motionDetector = null; // Initialized during init()
    this.enableMotionDetection = options.enableMotionDetection === true; // Default: false (too sensitive, blocks selection)

    // Initialize error handler and feedback overlay
    this._initializeErrorHandling();

    console.log('MultiMarkerARSession created');
  }

  /**
   * Initialize error handling components
   * @private
   */
  _initializeErrorHandling() {
    // Initialize error handler
    this.errorHandler = new ErrorHandler({
      onError: (error, level, userMessage, errorInfo) => {
        // Show error in feedback overlay
        if (this.feedbackOverlay && level !== 'info') {
          this.feedbackOverlay.showError(userMessage, errorInfo.troubleshooting);
        }

        // Call user callback if provided
        if (this.onError) {
          this.onError(error);
        }
      },
      showConsoleErrors: true,
    });

    // Initialize feedback overlay
    this.feedbackOverlay = new FeedbackOverlay({
      container: document.body,
      autoHide: false,
    });

    // Initialize audio consent dialog
    this.audioConsentDialog = new AudioConsentDialog();
    this.audioConsentDialog.init(document.body);

    console.log('✅ Error handling initialized');
  }

  /**
   * Initialize AR session
   * @returns {Promise<void>}
   */
  async init() {
    if (this.isInitialized) {
      console.warn('Session already initialized');
      return;
    }

    try {
      console.log('🚀 Initializing MultiMarkerARSession...');

      // Show loading overlay
      this.feedbackOverlay.showLoading('読み込み中...', 0);

      // Step 1: Load configuration
      this._reportProgress(10, 'Loading configuration...');
      this.feedbackOverlay.showLoading('読み込み中...', 10);
      this.config = await MarkerConfigLoader.load(this.configPath);
      console.log(
        `✅ Loaded config: ${this.config.posterId} with ${this.config.markers.length} markers`
      );

      // Step 2: Validate file paths
      this._reportProgress(20, 'Validating files...');
      this.feedbackOverlay.showLoading('読み込み中...', 20);
      const fileValidation = await MarkerConfigLoader.validateFilePaths(this.config);

      if (!fileValidation.mindFileExists) {
        const error = new Error(`Mind file not found: ${this.config.mindFilePath}`);
        this.errorHandler.handleError(error, ErrorType.MIND_FILE_NOT_FOUND, {
          mindFilePath: this.config.mindFilePath,
        });
        throw error;
      }

      const missingVideos = fileValidation.videos.filter((v) => !v.exists);
      if (missingVideos.length > 0) {
        console.warn(
          '⚠️ Missing videos:',
          missingVideos.map((v) => v.videoPath)
        );
        // Continue with available videos
      }

      // Step 3: Initialize detector
      this._reportProgress(30, 'Initializing detector...');
      this.feedbackOverlay.showLoading('読み込み中...', 30);
      this.detector = new PosterDetector({
        maxTargets: this.config.markers.length,
      });

      await this.detector.init(this.config.mindFilePath);
      await this.detector.initializeMultipleMarkers(this.config);
      console.log('✅ Detector initialized');

      // T031: Initialize object pools for Three.js resources
      this._initializeObjectPools();

      // T025: Detect device capabilities and set detection FPS
      const renderer = this.detector.getRenderer();
      if (renderer) {
        const gl = renderer.getContext();
        const capabilities = DeviceCapabilityDetector.detect(gl);
        this.deviceTier = capabilities.gpuTier;

        // Set detection FPS based on tier if not explicitly set
        if (!this.options.detectionFps) {
          const tierSettings = QUALITY_PRESETS[this.deviceTier];
          if (tierSettings) {
            this.detectionFps = tierSettings.detectionFps;
            console.log(
              `🔍 [T025] Detection FPS set to ${this.detectionFps} for ${this.deviceTier} tier device`
            );
          }
        }
      }

      // Step 4: Initialize video controller and preload
      this._reportProgress(40, 'Preloading videos...');
      this.feedbackOverlay.showLoading('読み込み中...', 40);

      // Check if Cloudflare Stream is configured and poster supports it
      const useCloudflareStream =
        isCloudflareStreamConfigured() && this.config.videoDelivery === 'cloudflare-stream';

      if (useCloudflareStream) {
        // Get adaptive config based on current network speed
        const streamConfig = getCloudflareStreamConfig(true); // true = use adaptive settings
        this.videoController = new CloudflareStreamVideoController({
          accountId: streamConfig.accountId,
          customerSubdomain: streamConfig.customerSubdomain,
          hlsConfig: streamConfig.hlsConfig,
        });
        console.log('🎬 Using CloudflareStreamVideoController with adaptive HLS settings');
        console.log(`📡 HLS buffer config: ${streamConfig.hlsConfig.maxBufferLength}s buffer`);
      } else {
        this.videoController = new MultiVideoController();
        console.log('📹 Using MultiVideoController with direct MP4 delivery');
      }

      await this.videoController.preloadAll(this.config.markers, (percent) => {
        // Map video preload progress to 40-70% range
        const mappedPercent = 40 + percent * 0.3;
        this._reportProgress(mappedPercent, 'Preloading videos...');
        this.feedbackOverlay.showLoading('読み込み中...', mappedPercent);
      });

      const stats = this.videoController.getStats();
      console.log(
        `✅ Videos preloaded: ${stats.loaded}/${stats.total} (${(stats.totalSize / 1024 / 1024).toFixed(2)} MB)`
      );

      // Show audio consent dialog if poster has audio
      console.log('🔍 [AUDIO DEBUG] Checking audioType:', this.config.audioType);
      if (this.config.audioType) {
        console.log('🔊 Showing audio consent dialog...');
        this.feedbackOverlay.hide(); // Hide loading overlay before showing dialog
        await this._showAudioConsentDialog();
        console.log('🔍 [AUDIO DEBUG] User audio choice after dialog:', this.userAudioChoice);
      } else {
        console.log('⚠️ [AUDIO DEBUG] No audioType in config - skipping audio consent dialog');
      }

      // Step 5: Initialize selection manager (silently after audio dialog)
      this._reportProgress(80, 'Initializing selection manager...');
      this.selectionManager = new ActiveMarkerSelectionManager({
        lockDuration: SELECTION_LOCK_DURATION, // T011: 800ms lock duration
        hysteresis: 50, // T010: 50px hysteresis threshold
        onMarkerChanged: (markerId) => this._switchToMarker(markerId),
      });
      console.log('✅ Selection manager initialized');

      // Step 6: Initialize Overlay
      this._initializeOverlay();

      // Step 7: Initialize camera motion detector
      if (this.enableMotionDetection) {
        this.motionDetector = new CameraMotionDetector({
          stabilityThreshold: this.config.motionDetection?.stabilityThreshold || 0.005,
          stabilityDuration: this.config.motionDetection?.stabilityDuration || 300,
        });
        console.log('✅ Camera motion detector initialized');
      }

      // Step 6: Initialize performance monitor (optional, silently)
      if (this.enablePerformanceMonitoring) {
        this._reportProgress(90, 'Initializing performance monitor...');
        this.performanceMonitor = new PerformanceMonitor({
          sampleInterval: 1000, // 1 second
          historySize: 60, // 60 seconds
          onMetricsUpdate: (metrics) => {
            // T040: Update debug visualizer with performance metrics
            if (this.debugVisualizer) {
              this.debugVisualizer.updatePerformanceMetrics(metrics);
            }
            // Call user's callback if provided
            if (this.onMetricsUpdate) {
              this.onMetricsUpdate(metrics);
            }
          },
          onPerformanceDegradation: (suggestedTier) => {
            // T041: Auto-fallback quality degradation
            this._handlePerformanceDegradation(suggestedTier);
            // Call user's callback if provided
            if (this.onPerformanceDegradation) {
              this.onPerformanceDegradation(suggestedTier);
            }
          },
        });
        console.log('✅ Performance monitor initialized');
      }

      // Step 7: Initialize debug visualizer (optional, silently)
      if (this.enableDebugVisualization) {
        this._reportProgress(95, 'Initializing debug visualizer...');
        this.debugVisualizer = new DebugVisualizer({
          showBounds: this.config.debug?.showMarkerBounds !== false,
          showPriorityScores: this.config.debug?.logPriorityScores !== false,
          showMarkerInfo: true,
        });
        console.log('✅ Debug visualizer initialized');
      }

      // Step 8: Setup callbacks
      this._setupDetectorCallbacks();

      this._reportProgress(100, 'Initialization complete');
      // Do NOT show loading overlay at 100% - keep it hidden after audio dialog

      // Hide loading after a brief moment
      setTimeout(() => {
        this.feedbackOverlay.hide();
      }, 500);

      // Initialize performance diagnostics overlay if debug mode
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('debug') === 'true' && this.detector.platform === 'Android') {
        console.log('📊 Initializing performance diagnostics overlay (Android debug mode)');
        getDiagnostics().init();
      }

      this.isInitialized = true;

      console.log('🎉 MultiMarkerARSession initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize session:', error);

      // Determine error type
      let errorType = ErrorType.INITIALIZATION_ERROR;
      if (error.message.includes('Mind file')) {
        errorType = ErrorType.MIND_FILE_LOAD_ERROR;
      } else if (error.message.includes('config')) {
        errorType = ErrorType.CONFIG_PARSE_ERROR;
      } else if (error.message.includes('video')) {
        errorType = ErrorType.VIDEO_LOAD_ERROR;
      }

      this.errorHandler.handleError(error, errorType, {
        configPath: this.configPath,
      });

      this._reportError(error);
      throw error;
    }
  }

  /**
   * Setup detector callbacks
   * @private
   */
  _setupDetectorCallbacks() {
    // Note: Individual marker detection/lost is handled in the detection loop
    // by checking marker states
  }

  // ... inside _detectionLoopTick or similar where markers are processed ...
  // We need to instantiate buttons when markers are first loaded/detected
  // Best place is probably in init() after loading config, or lazily.
  // Let's do it in init() after config load.

  /**
   * Initialize CTA overlay from config (structured cta or legacy overlayHtml)
   * @private
   */
  _initializeOverlay() {
    this.ctaOverlay = createCTAFromPosterData(this.config);
    if (!this.ctaOverlay) return;

    console.log('🔘 Initializing CTA overlay');

    const uiContainer = document.getElementById('ui-container');
    if (uiContainer) {
      this.ctaOverlay.mount(uiContainer);
    }
  }

  /**
   * Start AR session (detection and rendering loops)
   * @returns {Promise<void>}
   */
  async start() {
    if (!this.isInitialized) {
      throw new Error('Session not initialized. Call init() first.');
    }

    if (this.isRunning) {
      console.warn('Session already running');
      return;
    }

    try {
      // Reset selection manager to clear any previous state
      if (this.selectionManager) {
        this.selectionManager.reset();
        console.log('✅ Selection manager reset');
      }
      console.log('▶️ Starting AR session...');

      // Apply user's audio choice before starting
      console.log('🔍 [AUDIO DEBUG] Before applying audio:');
      console.log('  - userAudioChoice:', this.userAudioChoice);
      console.log('  - videoController exists:', !!this.videoController);

      if (this.userAudioChoice !== null && this.videoController) {
        console.log('🔊 Applying audio preference:', this.userAudioChoice ? 'muted' : 'unmuted');
        this.videoController.setMuted(this.userAudioChoice);
      } else {
        console.warn('⚠️ [AUDIO DEBUG] Cannot apply audio preference:');
        console.warn('  - userAudioChoice is null:', this.userAudioChoice === null);
        console.warn('  - videoController missing:', !this.videoController);
      }

      // Start MindAR detector
      await this.detector.start();

      // Start performance monitor
      if (this.performanceMonitor) {
        this.performanceMonitor.start();
      }

      // Enable debug visualizer
      if (this.debugVisualizer) {
        const scene = this.detector.getScene();
        const camera = this.detector.getCamera();
        this.debugVisualizer.enable(scene, camera);
      }

      // Set running state before starting loops
      this.isRunning = true;

      // Start detection loop (15fps)
      this._startDetectionLoop();

      // Start rendering loop (60fps)
      this._startRenderLoop();

      // Overlay visibility is now handled in detection loop

      // T033: Start inactive marker cleanup interval (every 5 seconds)
      this.inactiveCleanupInterval = setInterval(() => {
        this._checkInactiveMarkers();
      }, 5000);
      console.log('🧹 [T033] Inactive marker cleanup started (5s interval)');

      console.log('✅ AR session started');
    } catch (error) {
      console.error('❌ Failed to start session:', error);

      // Handle start errors - safely check error properties
      let errorType = ErrorType.INITIALIZATION_ERROR;
      const errorMessage = error?.message || error?.toString() || 'Unknown error';

      if (
        errorMessage.includes('camera') ||
        errorMessage.includes('video') ||
        errorMessage.includes('getUserMedia')
      ) {
        errorType = ErrorType.CAMERA_NOT_AVAILABLE;
      } else if (errorMessage.includes('Timeout')) {
        errorType = ErrorType.CAMERA_NOT_AVAILABLE;
      }

      // Create a proper error object if we don't have one
      const properError = error instanceof Error ? error : new Error(errorMessage);

      this.errorHandler.handleError(properError, errorType);
      this._reportError(properError);
      throw properError;
    }
  }

  /**
   * Start detection loop (low frequency for marker detection and priority calculation)
   * @private
   */
  _startDetectionLoop() {
    const intervalMs = 1000 / this.detectionFps;

    this.detectionInterval = setInterval(() => {
      this._detectionLoopTick();
    }, intervalMs);

    console.log(`🔍 Detection loop started at ${this.detectionFps} fps`);
  }

  /**
   * Detection loop tick (T020: 15fps detection, updates pose data)
   * @private
   */
  _detectionLoopTick() {
    try {
      const detectionStartTime = performance.now();

      // Update marker states from anchors
      this.detector.anchors.forEach((anchorData, index) => {
        this.detector.updateMarkerState(index, anchorData);
      });

      // Get tracking markers
      const trackingStates = this.detector.getTrackingMarkerStates();

      // T020: Update shared pose data for render loop
      // Wait for lock to be released (simple spin-lock)
      while (this.poseDataLock) {
        // Busy wait (acceptable since detection is 15fps)
      }

      this.poseDataLock = true;
      try {
        this.latestPoseData = {
          trackingStates: trackingStates.map((state) => ({
            markerId: state.markerId,
            matrix: state.anchor?.matrix ? state.anchor.matrix.clone() : null,
            worldPosition: state.worldPosition ? state.worldPosition.clone() : null,
            screenPosition: state.screenPosition ? { ...state.screenPosition } : null,
            distanceToCenter: state.distanceToCenter,
            confidence: state.confidence,
          })),
          timestamp: performance.now(),
        };
      } finally {
        this.poseDataLock = false;
      }

      // Log only when tracking count changes (reduce log spam)
      if (!this._lastTrackingCount || this._lastTrackingCount !== trackingStates.length) {
        console.log(
          `🔍 [TRACKING] ${trackingStates.length} marker(s) tracking (was: ${this._lastTrackingCount || 0})`
        );
        this._lastTrackingCount = trackingStates.length;
      }

      // Record detection for performance monitoring
      if (this.performanceMonitor) {
        const hasTracking = trackingStates.length > 0;
        this.performanceMonitor.recordDetection(hasTracking, trackingStates.length);
        this.performanceMonitor.updateMarkerStates(this.detector.getAllMarkerStates());

        // Record detection time
        const detectionDuration = performance.now() - detectionStartTime;
        this.performanceMonitor.recordDetectionTime(detectionDuration);
      }

      // Update CTA overlay visibility based on tracking
      if (this.ctaOverlay) {
        if (trackingStates.length > 0) {
          this.ctaOverlay.show();
        } else {
          this.ctaOverlay.hide();
        }
      }

      if (trackingStates.length === 0) {
        // No markers tracking
        if (this.selectionManager.getCurrentMarkerId()) {
          console.log('🔍 [TRACKING] Clearing selection - all markers lost');
          // Clear selection if all markers lost
          this.selectionManager.clearDetectedMarkers();
          // Use pauseAll() to stop ALL videos and update playbackStatus
          // This prevents unwanted loop restarts via ended event
          this.videoController.pauseAll();
        }
        return;
      }

      // T033: Update lastDetectedAt timestamps for all tracking markers
      const now = Date.now();
      trackingStates.forEach((state) => {
        this.markerLastDetectedAt.set(state.markerId, now);
      });

      // Camera motion detection - skip marker selection if camera is unstable
      const camera = this.detector.getCamera();
      if (this.enableMotionDetection && this.motionDetector) {
        const isCameraStable = this.motionDetector.update(camera);
        if (!isCameraStable) {
          console.log('🚫 Camera motion detected - skipping marker selection');
          return; // Skip selection during camera motion
        }
      }

      // Calculate priorities
      const viewport = this.detector.getRenderer()?.domElement
        ? {
            width: this.detector.getRenderer().domElement.width,
            height: this.detector.getRenderer().domElement.height,
          }
        : null;

      const scores = MarkerPriorityCalculator.calculate(trackingStates, camera, viewport);

      // Log scores in debug mode
      if (this.config.debug?.logPriorityScores) {
        const ranking = MarkerPriorityCalculator.getRanking(scores);
        console.log('📊 Priority ranking:', ranking);
      }

      // T042: Edge case detection - Check for equal distance markers (within hysteresis threshold)
      if (scores.size >= 2 && this.config.debug?.logPriorityScores) {
        const scoresArray = Array.from(scores.entries()).sort((a, b) => a[1] - b[1]);
        const [bestId, bestScore] = scoresArray[0];
        const [secondId, secondScore] = scoresArray[1];
        const scoreDiff = Math.abs(secondScore - bestScore);
        if (scoreDiff <= this.selectionManager.hysteresis) {
          console.log(
            `⚖️ [T042 EDGE] Equal distance markers: ${bestId} (${bestScore.toFixed(1)}px) vs ${secondId} (${secondScore.toFixed(1)}px), diff=${scoreDiff.toFixed(1)}px ≤ ${this.selectionManager.hysteresis}px (hysteresis protects current)`
          );
        }
      }

      // Select active marker
      const currentMarkerId = this.selectionManager.getCurrentMarkerId();
      const bestMarkerId = MarkerPriorityCalculator.selectActive(scores, currentMarkerId);

      console.log(
        `🔍 [SELECTION] Tracking: ${trackingStates.length} | Current: ${currentMarkerId || 'none'} | Best: ${bestMarkerId || 'none'}`
      );

      if (!bestMarkerId) {
        return;
      }

      const bestScore = scores.get(bestMarkerId);
      debugLog(`🔍 [DEBUG] Best score: ${bestScore?.toFixed(2)}`);

      // Record selection time
      const selectionStartTime = performance.now();

      // Update selection manager
      const changed = this.selectionManager.update(bestMarkerId, bestScore);
      console.log(`🔍 [SELECTION] Changed: ${changed} | Will switch: ${changed ? 'YES' : 'NO'}`);

      // Performance diagnostics overlay (Android only)
      if (changed && this.detector.platform === 'Android') {
        getDiagnostics().logEvent('SELECTION', `Marker selected: ${bestMarkerId}`, {
          score: bestScore.toFixed(1),
        });
      }

      if (changed) {
        console.log(`🎬 [SELECTION] >>> Calling _switchToMarker(${bestMarkerId})`);
        // Switch video
        this._switchToMarker(bestMarkerId);
        console.log(`🎬 [SELECTION] <<< _switchToMarker() call completed`);
      }

      // Record selection time
      if (this.performanceMonitor) {
        const selectionDuration = performance.now() - selectionStartTime;
        this.performanceMonitor.recordSelectionTime(selectionDuration);
      }

      // Update debug visualizer
      if (this.debugVisualizer) {
        const currentMarkerId = this.selectionManager.getCurrentMarkerId();
        this.debugVisualizer.update(this.detector.getAllMarkerStates(), scores, currentMarkerId);
      }
    } catch (error) {
      console.error('Error in detection loop:', error);
    }
  }

  /**
   * Switch to a different marker's video (synchronous for fast switching)
   * @param {string} markerId - Target marker ID
   * @private
   */
  _switchToMarker(markerId) {
    try {
      debugLog(`🎬 [DEBUG] _switchToMarker called for: ${markerId}`);
      const switchStartTime = performance.now();

      // Performance diagnostics overlay (Android only)
      if (this.detector?.platform === 'Android') {
        const prevMarkerId = this.currentActiveMarkerId;
        getDiagnostics().logEvent('SWITCH_START', `${prevMarkerId || 'none'} → ${markerId}`, {
          platform: this.detector.platform,
        });
      }

      // Hide ALL marker planes first to prevent "ghosting" or overlap
      // This ensures a clean slate before the new video starts
      this.detector.getAllMarkerStates().forEach((state) => {
        if (state.anchor && state.anchor.group.children.length > 0) {
          const plane = state.anchor.group.children[0];
          if (plane.visible) {
            plane.visible = false;
            debugLog(`🎬 [DEBUG] Hidden plane for: ${state.markerId}`);
          }
        }
      });

      // Android-specific blackout delay to ensure render buffer clears
      // This prevents the "ghosting" effect where the old video frame persists
      const isAndroid = this.detector?.platform === 'Android';
      // Final Fix: Increased delay to 300ms and use isSwitching flag
      const switchDelay = isAndroid ? 300 : 0;

      // Set switching flag to true - this will be checked by render loop
      // to enforce blackout during the transition
      this.isSwitching = true;

      // Immediately pause current video to stop audio and processing
      if (this.videoController) {
        this.videoController.pause();
      }

      if (isAndroid) {
        debugLog(`🎬 [DEBUG] Android detected: Applying ${switchDelay}ms blackout delay`);
      }

      setTimeout(() => {
        try {
          const asset = this.videoController.switchTo(markerId);
          debugLog(`🎬 [DEBUG] Video controller switched, asset:`, asset);

          // Update and show video plane for selected marker
          const markerState = this.detector.getMarkerState(markerId);
          debugLog(`🎬 [DEBUG] Marker state:`, markerState ? 'exists' : 'null');

          if (markerState && markerState.anchor) {
            debugLog(`🎬 [DEBUG] Anchor exists, updating video plane`);
            // Update video plane with new texture and marker ID for bounds
            this._updateVideoPlane(markerState.anchor, asset.texture, markerId);

            // Make sure the active marker's plane is visible
            if (markerState.anchor.group.children.length > 0) {
              const plane = markerState.anchor.group.children[0];
              plane.visible = true;
              debugLog(`🎬 [DEBUG] Showing plane for active marker: ${markerId}`);
            }

            // Update current active marker ID
            this.currentActiveMarkerId = markerId;
          } else {
            debugWarn(`🎬 [DEBUG] No marker state or anchor for ${markerId}`);
          }

          // Record video switch for performance monitoring
          if (this.performanceMonitor) {
            this.performanceMonitor.recordVideoSwitch(markerId);
          }

          // Notify callback
          if (this.onMarkerChanged) {
            this.onMarkerChanged(markerId);
          }

          // Performance diagnostics overlay (Android only)
          if (this.detector?.platform === 'Android') {
            const switchDuration = performance.now() - switchStartTime;
            getDiagnostics().logEvent(
              'SWITCH_COMPLETE',
              `Completed in ${switchDuration.toFixed(1)}ms`,
              {
                markerId,
                duration: `${switchDuration.toFixed(1)}ms`,
              }
            );
          }

          // Reset switching flag
          this.isSwitching = false;

          console.log(`🎬 Switched to marker: ${markerId}`);
        } catch (error) {
          console.error(`❌ Failed to switch to marker ${markerId} (delayed):`, error);
          this.isSwitching = false; // Ensure flag is reset even on error
        }
      }, switchDelay);

      // Performance diagnostics overlay (Android only)
      if (this.detector?.platform === 'Android') {
        const switchDuration = performance.now() - switchStartTime;
        getDiagnostics().logEvent(
          'SWITCH_COMPLETE',
          `Completed in ${switchDuration.toFixed(1)}ms`,
          {
            markerId,
            duration: `${switchDuration.toFixed(1)}ms`,
          }
        );
      }

      console.log(`🎬 Switched to marker: ${markerId}`);
    } catch (error) {
      console.error(`❌ Failed to switch to marker ${markerId}:`, error);
    }
  }

  /**
   * Create video plane for a marker
   * @private
   * @param {Object} marker - Marker configuration
   * @returns {THREE.Mesh} Video plane mesh
   */
  _createVideoPlane(marker) {
    // Use aspect ratio from config (default 0.707 for A4)
    // Width is 1 (normalized to marker width), height is calculated
    const aspectRatio = this.config.aspectRatio || 0.707; // Height / Width
    debugLog(`🔍 [DEBUG] Creating video plane for ${marker.id}. AspectRatio: ${aspectRatio}`);

    const geometry = new THREE.PlaneGeometry(1, aspectRatio);

    // Create video material
    const material = this.videoController.createVideoMaterial(marker.id);

    const plane = new THREE.Mesh(geometry, material);

    // Center the plane on the marker
    plane.position.set(0, 0, 0);

    return plane;
  }

  /**
   * Update video plane with new texture
   * @param {Object} anchor - MindAR anchor
   * @param {THREE.VideoTexture} texture - Video texture
   * @param {string} markerId - Marker ID to get bounds from config
   * @private
   */
  _updateVideoPlane(anchor, texture, markerId) {
    debugLog(`📺 [DEBUG] _updateVideoPlane called for marker: ${markerId}`);
    debugLog(`📺 [DEBUG] Anchor group children:`, anchor.group.children.length);
    debugLog(`📺 [DEBUG] Texture:`, texture);

    // Find or create plane geometry for video
    if (!anchor.group.children.length) {
      debugLog(`📺 [DEBUG] Creating new plane`);

      // Get marker definition from config to find targetIndex
      const markerDef = this.config.markers.find((m) => m.id === markerId);
      if (!markerDef) {
        console.error(`📺 [ERROR] No marker definition found for: ${markerId}`);
        return;
      }

      // Get video dimensions from texture
      const video = texture.image;
      const videoAspect = video.videoWidth / video.videoHeight;

      // Get actual marker dimensions from MindAR (extracted from .mind file)
      const targetDimensions = this.detector.getTargetDimensions();
      const targetDim = targetDimensions[markerDef.targetIndex];

      if (!targetDim) {
        console.error(`📺 [ERROR] No target dimensions for targetIndex: ${markerDef.targetIndex}`);
        return;
      }

      const markerAspect = targetDim.aspectRatio;

      debugLog(
        `📺 [DEBUG] Video: ${video.videoWidth}x${video.videoHeight} (aspect: ${videoAspect.toFixed(3)})`
      );
      debugLog(`📺 [DEBUG] Marker aspect (from .mind): ${markerAspect.toFixed(3)}`);
      debugLog(`📺 [DEBUG] Target index: ${markerDef.targetIndex}`);

      // Use normalized size (width = 1.0) for proper scaling
      // MindAR handles the actual world-space scaling
      const normalizedWidth = 1.0;
      const normalizedHeight = normalizedWidth / markerAspect;

      let planeWidth, planeHeight;

      // Fit video inside marker bounds (contain mode)
      if (videoAspect > markerAspect) {
        // Video is wider than marker - fit to width
        planeWidth = normalizedWidth;
        planeHeight = planeWidth / videoAspect;
      } else {
        // Video is taller than marker - fit to height
        planeHeight = normalizedHeight;
        planeWidth = planeHeight * videoAspect;
      }

      debugLog(`📺 [DEBUG] Plane dimensions: ${planeWidth.toFixed(3)}x${planeHeight.toFixed(3)}`);

      // T031: Acquire geometry and material from object pools
      const geometry = this.geometryPool.acquire();
      geometry.copy(new THREE.PlaneGeometry(planeWidth, planeHeight)); // Update pool geometry with correct size

      const material = this.materialPool.acquire();
      material.map = texture;
      material.transparent = false;
      material.side = THREE.DoubleSide;
      material.needsUpdate = true;

      const plane = new THREE.Mesh(geometry, material);

      console.log(
        `🔄 [T031] Geometry and material acquired from pools (${this.geometryPool.getStats().inUse} geo, ${this.materialPool.getStats().inUse} mat in use)`
      );

      // Position plane at marker origin (0, 0)
      // Each anchor.group is already positioned at the marker location
      // so we don't need to offset by bounds.x/bounds.y
      plane.position.set(0, 0, 0.01);

      debugLog(`📺 [DEBUG] Plane position: (0, 0, 0.01) - centered at marker origin`);

      // Ensure texture updates
      texture.needsUpdate = true;
      material.needsUpdate = true;

      anchor.group.add(plane);
      debugLog(`📺 [DEBUG] Plane added to anchor group`);
    } else {
      debugLog(`📺 [DEBUG] Updating existing plane texture`);

      // Update existing plane texture
      const plane = anchor.group.children[0];
      if (plane.material) {
        plane.material.map = texture;
        plane.material.needsUpdate = true;
        texture.needsUpdate = true;
        debugLog(`📺 [DEBUG] Texture updated on existing plane`);
      } else {
        debugWarn(`📺 [DEBUG] Plane has no material!`);
      }
    }
  }

  /**
   * T033: Check and clean up inactive markers
   * Removes resources for markers not detected in the last 30 seconds
   * @private
   */
  _checkInactiveMarkers() {
    const now = Date.now();
    const inactiveThreshold = 30000; // 30 seconds

    this.markerLastDetectedAt.forEach((lastDetectedAt, markerId) => {
      const timeSinceLastDetection = now - lastDetectedAt;

      if (timeSinceLastDetection > inactiveThreshold) {
        console.log(
          `🧹 [T033] Cleaning up inactive marker: ${markerId} (${(timeSinceLastDetection / 1000).toFixed(1)}s since last detection)`
        );

        // Get marker state
        const markerState = this.detector.getMarkerState(markerId);
        if (markerState && markerState.anchor) {
          // Release plane resources to pool
          this._releasePlaneResources(markerState.anchor);
        }

        // Clear video texture tracking
        const videoAsset = this.videoController.getVideoAsset(markerId);
        if (videoAsset && videoAsset.videoElement) {
          this.videoTextureManager.clearVideo(videoAsset.videoElement);

          // Pause video to save resources
          videoAsset.videoElement.pause();
        }

        // Remove from tracking
        this.markerLastDetectedAt.delete(markerId);

        console.log(`✅ [T033] Marker ${markerId} resources cleaned up`);
      }
    });
  }

  /**
   * T031: Release video plane resources back to object pools
   * @param {Object} anchor - MindAR anchor
   * @private
   */
  _releasePlaneResources(anchor) {
    if (anchor && anchor.group && anchor.group.children.length > 0) {
      const plane = anchor.group.children[0];

      if (plane.geometry) {
        this.geometryPool.release(plane.geometry);
      }

      if (plane.material) {
        // Don't release texture (managed by VideoController)
        plane.material.map = null;
        this.materialPool.release(plane.material);
      }

      anchor.group.remove(plane);

      console.log(
        `🔄 [T031] Plane resources released to pools (${this.geometryPool.getStats().inUse} geo, ${this.materialPool.getStats().inUse} mat in use)`
      );
    }
  }

  /**
   * Start rendering loop (T020: 60fps rendering, reads latest pose data)
   * @private
   */
  _startRenderLoop() {
    const renderTick = () => {
      if (!this.isRunning) return;

      try {
        const frameStartTime = performance.now();

        // T020: Read latest pose data (non-blocking)
        let poseData = null;
        if (!this.poseDataLock && this.latestPoseData) {
          this.poseDataLock = true;
          try {
            poseData = this.latestPoseData;
          } finally {
            this.poseDataLock = false;
          }
        }

        // Final Fix: Enforce blackout during switching
        // If switching, force ALL planes to be invisible regardless of tracking state
        if (this.isSwitching) {
          this.detector.getAllMarkerStates().forEach((state) => {
            if (state.anchor && state.anchor.group.children.length > 0) {
              const plane = state.anchor.group.children[0];
              if (plane.visible) {
                plane.visible = false;
              }
            }
          });
        } else if (poseData && poseData.trackingStates) {
          // Apply pose data to anchors if available (only if NOT switching)
          poseData.trackingStates.forEach((state) => {
            if (state.matrix) {
              const markerState = this.detector.getMarkerState(state.markerId);
              if (markerState && markerState.anchor && markerState.anchor.group) {
                // Update anchor group matrix from latest detection
                markerState.anchor.group.matrix.copy(state.matrix);
                markerState.anchor.group.matrixWorldNeedsUpdate = true;
              }
            }
          });
        }

        // T032: Update video textures conditionally - ONLY active marker for performance
        // This reduces GPU overhead by 70% (from updating 3-5 markers to just 1 per frame)
        if (poseData && poseData.trackingStates && this.currentActiveMarkerId) {
          // Find active marker in tracking states
          const activeState = poseData.trackingStates.find(
            (state) => state.markerId === this.currentActiveMarkerId
          );

          if (activeState) {
            const markerState = this.detector.getMarkerState(activeState.markerId);
            if (markerState && markerState.anchor && markerState.anchor.group.children.length > 0) {
              const plane = markerState.anchor.group.children[0];
              if (plane.material && plane.material.map && plane.material.map.image) {
                const videoElement = plane.material.map.image;
                this.videoTextureManager.updateTexture(plane.material.map, videoElement);
              }
            }
          }
        }

        // Render Three.js scene
        const renderer = this.detector.getRenderer();
        const scene = this.detector.getScene();
        const camera = this.detector.getCamera();

        if (renderer && scene && camera) {
          renderer.render(scene, camera);
        }

        // Record frame for performance monitoring
        if (this.performanceMonitor) {
          const frameDuration = performance.now() - frameStartTime;
          this.performanceMonitor.recordFrame();
          this.performanceMonitor.recordFrameTime(frameDuration);
        }
      } catch (error) {
        console.error('Error in render loop:', error);
      }

      this.renderLoopId = requestAnimationFrame(renderTick);
    };

    renderTick();
    console.log('🎨 Render loop started at 60 fps (independent from detection)');
  }

  /**
   * Stop AR session
   */
  stop() {
    if (!this.isRunning) {
      return;
    }

    console.log('⏸️ Stopping AR session...');

    // Stop detection loop
    if (this.detectionInterval) {
      clearInterval(this.detectionInterval);
      this.detectionInterval = null;
    }

    // Stop render loop
    if (this.renderLoopId) {
      cancelAnimationFrame(this.renderLoopId);
      this.renderLoopId = null;
    }

    // T033: Stop inactive marker cleanup
    if (this.inactiveCleanupInterval) {
      clearInterval(this.inactiveCleanupInterval);
      this.inactiveCleanupInterval = null;
      console.log('🧹 [T033] Inactive marker cleanup stopped');
    }

    // T020: Clear shared pose data
    this.latestPoseData = null;
    this.poseDataLock = false;

    // Stop detector
    if (this.detector) {
      this.detector.stop();
    }

    // Pause videos
    if (this.videoController) {
      this.videoController.pause();
    }

    // Stop performance monitor
    if (this.performanceMonitor) {
      this.performanceMonitor.stop();
    }

    // Reset camera motion detector
    if (this.motionDetector) {
      this.motionDetector.reset();
    }

    // Disable debug visualizer
    if (this.debugVisualizer) {
      this.debugVisualizer.disable();
    }

    // Stop detection monitoring
    if (this.feedbackOverlay) {
      this.feedbackOverlay.stopDetectionMonitoring();
      this.feedbackOverlay.hide();
    }

    this.isRunning = false;
    console.log('✅ AR session stopped');
  }

  /**
   * Get current configuration
   * @returns {Object} MarkerConfig
   */
  getConfig() {
    return this.config;
  }

  /**
   * Get current active marker ID
   * @returns {string|null}
   */
  getCurrentMarkerId() {
    return this.selectionManager ? this.selectionManager.getCurrentMarkerId() : null;
  }

  /**
   * Get session statistics
   * @returns {Object}
   */
  getStats() {
    const videoStats = this.videoController ? this.videoController.getStats() : null;
    const selectionState = this.selectionManager ? this.selectionManager.getState() : null;
    const markerStates = this.detector ? this.detector.getAllMarkerStates() : [];
    const performanceMetrics = this.performanceMonitor
      ? this.performanceMonitor.getMetrics()
      : null;

    return {
      isInitialized: this.isInitialized,
      isRunning: this.isRunning,
      videoStats,
      selectionState,
      trackingCount: markerStates.filter((s) => s.trackingStatus === 'tracking').length,
      totalMarkers: markerStates.length,
      performance: performanceMetrics,
    };
  }

  /**
   * Get performance report
   * @returns {Object|null} Performance report or null if monitoring disabled
   */
  getPerformanceReport() {
    return this.performanceMonitor ? this.performanceMonitor.getReport() : null;
  }

  /**
   * Check performance health
   * @param {Object} thresholds - Optional thresholds
   * @returns {Object|null} Health status or null if monitoring disabled
   */
  checkPerformanceHealth(thresholds) {
    return this.performanceMonitor ? this.performanceMonitor.checkHealth(thresholds) : null;
  }

  /**
   * T031: Initialize Three.js object pools
   * @private
   */
  _initializeObjectPools() {
    const maxMarkers = this.config ? this.config.markers.length : 5;

    // Geometry pool for video planes
    this.geometryPool = new ObjectPool(() => {
      return new THREE.PlaneGeometry(1, 1);
    }, maxMarkers);

    // Material pool for video textures
    this.materialPool = new ObjectPool(() => {
      return new THREE.MeshBasicMaterial({
        transparent: false,
        side: THREE.DoubleSide,
      });
    }, maxMarkers);

    console.log(`🔄 [T031] Object pools initialized (${maxMarkers} slots each)`);
  }

  /**
   * Report progress
   * @param {number} percent - Progress percentage (0-100)
   * @param {string} message - Progress message
   * @private
   */
  _reportProgress(percent, message) {
    if (this.onProgress) {
      this.onProgress(percent, message);
    }
  }

  /**
   * Report error
   * @param {Error} error - Error object
   * @private
   */
  _reportError(error) {
    if (this.onError) {
      this.onError(error);
    }
  }

  /**
   * Show audio consent dialog and wait for user choice
   * @private
   * @returns {Promise<void>}
   */
  async _showAudioConsentDialog() {
    return new Promise((resolve) => {
      this.audioConsentDialog.show((isMuted) => {
        this.userAudioChoice = isMuted;
        console.log(`✅ User chose audio: ${isMuted ? 'muted' : 'unmuted'}`);
        resolve();
      });
    });
  }

  /**
   * T041: Handle performance degradation with automatic quality fallback
   * @param {string} suggestedTier - Suggested quality tier ('high', 'mid', 'low')
   * @private
   */
  _handlePerformanceDegradation(suggestedTier) {
    // Map suggested tier to actual tier name
    const tierMap = {
      high: 'high',
      mid: 'mid',
      low: 'low',
    };

    const targetTier = tierMap[suggestedTier] || 'low';

    // Don't downgrade if already at target tier or lower
    const tierOrder = { high: 3, mid: 2, low: 1 };
    if (tierOrder[this.deviceTier] <= tierOrder[targetTier]) {
      console.log(
        `ℹ️ [T041] Already at ${this.deviceTier} tier, skipping degradation to ${targetTier}`
      );
      return;
    }

    console.warn(
      `⚠️ [T041] Performance degradation detected, downgrading from ${this.deviceTier} to ${targetTier} tier`
    );

    // Get quality preset for target tier
    const tierSettings = QUALITY_PRESETS[targetTier];
    if (!tierSettings) {
      console.error(`❌ [T041] Invalid tier settings for ${targetTier}`);
      return;
    }

    // Update detection FPS
    const oldDetectionFps = this.detectionFps;
    this.detectionFps = tierSettings.detectionFps;

    // Restart detection loop with new FPS
    if (this.detectionInterval) {
      clearInterval(this.detectionInterval);
      this._startDetectionLoop();
    }

    // Update device tier
    this.deviceTier = targetTier;

    console.log(
      `✅ [T041] Quality downgraded: detection FPS ${oldDetectionFps} → ${this.detectionFps}`
    );

    // Show user notification via feedback overlay (optional)
    if (this.feedbackOverlay) {
      this.feedbackOverlay.showInfo('デバイスに最適化しました', 3000);
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stop();

    if (this.detector) {
      this.detector.cleanup();
      this.detector = null;
    }

    if (this.videoController) {
      this.videoController.cleanup();
      this.videoController = null;
    }

    if (this.selectionManager) {
      this.selectionManager.reset();
      this.selectionManager = null;
    }

    if (this.performanceMonitor) {
      this.performanceMonitor = null;
    }

    if (this.debugVisualizer) {
      this.debugVisualizer.cleanup();
      this.debugVisualizer = null;
    }

    if (this.feedbackOverlay) {
      this.feedbackOverlay.cleanup();
      this.feedbackOverlay = null;
    }

    if (this.audioConsentDialog) {
      this.audioConsentDialog.cleanup();
      this.audioConsentDialog = null;
    }

    if (this.ctaOverlay) {
      this.ctaOverlay.cleanup();
      this.ctaOverlay = null;
    }

    if (this.errorHandler) {
      this.errorHandler.clearHistory();
      this.errorHandler = null;
    }

    // T031: Dispose object pools
    if (this.geometryPool) {
      this.geometryPool.dispose();
      this.geometryPool = null;
    }
    if (this.materialPool) {
      this.materialPool.dispose();
      this.materialPool = null;
    }

    // T032: Clear video texture manager
    if (this.videoTextureManager) {
      this.videoTextureManager.clearAll();
      this.videoTextureManager = null;
    }

    this.config = null;
    this.isInitialized = false;

    console.log('🧹 MultiMarkerARSession cleaned up');
  }
}
